/**
 * Attesa di un job di anonimizzazione.
 *
 * Vive in un modulo suo per due motivi: e' la logica che, sbagliata, fa
 * perdere un documento gia' pagato, e dentro ObscurIALanding.jsx non era
 * testabile (quel file tira dentro pdf.js, che in jsdom non si carica).
 *
 * Il tempo e la rete si possono iniettare, cosi' i test verificano il
 * comportamento vero senza aspettare minuti.
 */

const DEFAULT_P = 5000;
// Fermo: nessun cambiamento di stato per tutto questo tempo -> ci si arrende.
const DEFAULT_FERMO_MS = 10 * 60 * 1000;
// Tetto assoluto: esiste solo per non lasciare cicli eterni in una scheda
// dimenticata, non per limitare i lavori lunghi.
const DEFAULT_TETTO_MS = 45 * 60 * 1000;

const STATI_FALLITI = ["failed", "error", "cancelled", "timeout"];

/**
 * Aspetta che un job risulti completato e ne restituisce i risultati.
 *
 * Ci si arrende per IMMOBILITA', non per numero di tentativi. Il limite
 * precedente era fisso - 60 tentativi ogni 5 secondi, cioe' 5 minuti - ed era
 * tarato su un documento solo. Ma il worker elabora i job in sequenza e tutti
 * i cicli di attesa partono insieme: con dieci file l'ultimo entra in
 * lavorazione quando i primi nove sono finiti. A 26 secondi a documento
 * (misurati su un referto scansionato di 6 pagine) il decimo chiude verso i
 * 260 secondi contro un limite di 300; con documenti piu' pesanti il limite
 * scadeva mentre il backend stava ancora lavorando, e il risultato - pagato -
 * veniva buttato.
 *
 * @param {string} jobId
 * @param {(msg: string) => void} [onStatus]
 * @param {{
 *   P?: number, fermoMs?: number, tettoMs?: number,
 *   now?: () => number, sleep?: (ms: number) => Promise<void>,
 *   fetchFn?: typeof fetch,
 *   httpError?: (status: number, contesto: string) => Error,
 *   friendlyMessage?: (msg: string, contesto: string) => Error,
 * }} [opts]
 */
export async function pollJobPreviewUntilDone(jobId, onStatus, opts = {}) {
  const {
    P = DEFAULT_P,
    fermoMs = DEFAULT_FERMO_MS,
    tettoMs = DEFAULT_TETTO_MS,
    now = () => Date.now(),
    sleep = (ms) => new Promise((res) => setTimeout(res, ms)),
    fetchFn = (...a) => fetch(...a),
    httpError = (status) => Object.assign(new Error(`HTTP ${status}`), { status }),
    friendlyMessage = (msg) => new Error(msg),
  } = opts;

  const url = `/api/jobs/${jobId}/preview`;
  const avvio = now();
  let ultimoCambio = now();
  let impronta = null;
  let primo = true;

  for (;;) {
    if (onStatus && primo) { onStatus("Elaborazione in corso..."); primo = false; }

    let d = null;
    try {
      const r = await fetchFn(url);
      if (r.ok) {
        d = await r.json();
      } else if (r.status === 401 || r.status === 403) {
        throw httpError(r.status, "job");
      } else if (r.status === 413) {
        throw httpError(413, "job");
      }
      // Altri errori HTTP: puo' essere un intoppo momentaneo del proxy. Non si
      // butta il job, si riprova finche' resta tempo.
    } catch (e) {
      // Un errore di rete durante un'attesa di minuti e' quasi sempre
      // passeggero: rilanciarlo farebbe perdere un documento in lavorazione.
      // Vale come "nessuna notizia". Gli errori HTTP costruiti qui sopra
      // portano uno `status` e vanno invece propagati.
      if (e && typeof e.status === "number") throw e;
    }

    if (d) {
      if (d.progress >= 100 || d.status === "completed") {
        const arr = Array.isArray(d?.result?.results) ? d.result.results : [];
        return arr.map((item) => (
          typeof item === "object" && item ? { ...item, obscura_job_id: jobId } : item
        ));
      }
      if (STATI_FALLITI.includes(String(d.status).toLowerCase())) {
        throw friendlyMessage(
          "Elaborazione di un file non riuscita. Verifica che i file caricati siano validi e riprova.",
          "job",
        );
      }
      const ora = `${d.status}|${d.progress}|${d.progress_message || ""}`;
      if (ora !== impronta) { impronta = ora; ultimoCambio = now(); }
    }

    if (now() - ultimoCambio > fermoMs || now() - avvio > tettoMs) {
      throw friendlyMessage(
        "L'elaborazione sta impiegando più del previsto. Riprova o contattaci se il problema persiste.",
        "job",
      );
    }
    await sleep(P);
  }
}
